import { ReactNode, forwardRef, useEffect, useLayoutEffect, useState } from 'react'

import { Portal } from 'ui/components/Portal'
import { useDelayMount } from 'ui/hooks/useDelayMount'

import { IconName } from '../Icon'
import { FloatingPanelContext } from './context'

import * as SC from './styles'

type Props = {
  isEnabled?: boolean
  isShown?: boolean
  title?: string
  actions?: ReactNode
  content: ReactNode

  onEnabled?: (isEnabled: boolean) => void
  onShowHide?: (isShown: boolean) => void
}

export const FloatingPanel = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    isEnabled: _isEnabled = false,
    isShown: _isShown = true,
    title,
    actions,
    content,
    onEnabled,
    onShowHide
  } = props

  const [isEnabled, setIsEnabled] = useState(_isEnabled)
  const [isShown, setIsShown] = useState(_isShown)
  const [isAnimating, setIsAnimating] = useState(false)

  const isEnablePanel = useDelayMount(isEnabled, 250)

  useEffect(() => {
    setIsEnabled(_isEnabled)
  }, [_isEnabled])

  useLayoutEffect(() => {
    if (onEnabled) {
      onEnabled(isEnablePanel)
    }
  }, [onEnabled, isEnablePanel])

  const showAction = () => {
    setIsShown(true)
    if (onShowHide) {
      onShowHide(true)
    }
  }

  const hideAction = () => {
    setIsShown(false)
    if (onShowHide) {
      onShowHide(false)
    }
  }

  const handleToggleShown = () => {
    setIsAnimating(true)
    setIsShown(!isShown)
    setTimeout(() => {
      setIsAnimating(false)
      if (onShowHide) {
        onShowHide(!isShown)
      }
    }, 250)
  }

  return (
    <>
      {isEnablePanel && (
        <FloatingPanelContext.Provider
          value={{
            isEnabled,
            isShown,
            show: showAction,
            hide: hideAction
          }}
        >
          <Portal id={'floating-panel'}>
            <SC.Box $enabled={isEnabled} ref={ref}>
              <SC.Header $shown={isShown} onClick={handleToggleShown}>
                <SC.CloseIcon name={isShown ? IconName.SCHEVRON_DOWN : IconName.SCHEVRON_UP} />
                <SC.Title>{title}</SC.Title>
                <SC.Actions>{actions}</SC.Actions>
              </SC.Header>
              <SC.Content
                className={isShown ? 'shown' : 'hidden'}
                style={{ overflowY: isAnimating ? 'hidden' : 'auto' }}
              >
                <SC.ContentInside>{content}</SC.ContentInside>
              </SC.Content>
            </SC.Box>
          </Portal>
        </FloatingPanelContext.Provider>
      )}
    </>
  )
})
